import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { dhfApi } from '../../utils/dhfApi';
import { Chip, coverageChip, testsChip } from './dhfUi';
import { Diff } from './variantsUi';
import '../../styles/DocReview.css';
import '../../styles/Dhf.css';
import '../../styles/DhfVariants.css';

const FILTERS = [
  { key: 'diff', label: 'Only differences', test: (r) => r.status !== 'same' },
  { key: 'all', label: 'All', test: () => true },
  { key: 'changed', label: 'Text', test: (r) => r.status === 'changed' },
  { key: 'only', label: 'Added / removed', test: (r) => r.status === 'only_a' || r.status === 'only_b' },
];

function cell(side, other, row, which) {
  if (!side) {
    return <span className="dhfv-absent">— {row.removed_in === which ? 'removed in this branch' : 'not in this one'}</span>;
  }
  if (row.status === 'changed') {
    const segs = row.diff;
    // A → B diff: A's cell shows what B dropped, B's cell what B added.
    const keep = which === 'a' ? (s) => s.op !== 'ins' : (s) => s.op !== 'del';
    return (
      <>
        <div className="dhfv-cmp-name"><Diff segs={(segs.name || []).filter(keep)} /></div>
        <div><Diff segs={(segs.description || []).filter(keep)} /></div>
      </>
    );
  }
  return (
    <>
      <div className="dhfv-cmp-name">{side.name}</div>
      <div className="dhfv-cmp-desc">{(side.description || '').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()}</div>
    </>
  );
}

/**
 * Compare any two of {origin, branches} of one document side by side (mockup screen 3, D5).
 * "Copy to …" moves an item between branches — the trace tool only; only Merge writes Orcanos (D7).
 */
export default function DhfVariantComparePage() {
  const { setupId, docId } = useParams();
  const [sp, setSp] = useSearchParams();
  const a = sp.get('a') || 'origin';
  const b = sp.get('b') || '';
  const [list, setList] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('diff');
  const [note, setNote] = useState('');

  useEffect(() => {
    dhfApi.variants(setupId, docId).then(setList).catch((e) => setError(e.message));
  }, [setupId, docId]);

  const load = useCallback(async () => {
    if (!b || a === b) { setData(null); return; }
    try {
      setData(await dhfApi.compareVariants(setupId, docId, a, b));
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, [setupId, docId, a, b]);
  useEffect(() => { load(); }, [load]);

  const options = useMemo(() => [{ id: 'origin', name: 'Origin (Orcanos)' },
    ...((list?.variants) || []).map((v) => ({ id: v.id, name: v.name }))], [list]);
  const label = (id) => options.find((o) => o.id === id)?.name || id;
  const pick = (k, v) => { const n = new URLSearchParams(sp); n.set(k, v); setSp(n, { replace: true }); };

  const copy = async (row, from, to) => {
    const src = from === 'a' ? row.a : row.b;
    const target = to === 'a' ? a : b;
    try {
      await dhfApi.copyNode(setupId, target, { source: from === 'a' ? a : b, nid: src.nid });
      setNote(`Copied “${src.name}” to ${label(target)}.`);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const f = FILTERS.find((x) => x.key === filter);
  const rows = (data?.rows || []).filter(f.test);
  const s = data?.summary;

  return (
    <div className="drv-page">
      <div className="dhf-doc-bar">
        <div className="dhf-doc-bar-l">
          <Link to={`/dhf/${setupId}/doc/${docId}/variants`}>← Branches</Link>
          <strong>Compare — {list?.origin?.name || ''}</strong>
        </div>
      </div>

      <div className="drv-card dhfv-cmp-head">
        <div className="dhfv-cmp-pick">
          <label>Left
            <select value={a} onChange={(e) => pick('a', e.target.value)}>
              {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </label>
          <button type="button" className="drv-btn" aria-label="Swap sides" onClick={() => { const n = new URLSearchParams(sp); n.set('a', b); n.set('b', a); setSp(n, { replace: true }); }}>⇄</button>
          <label>Right
            <select value={b} onChange={(e) => pick('b', e.target.value)}>
              <option value="" disabled>Choose…</option>
              {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </label>
          <div className="dhf-filters">
            {FILTERS.map((x) => (
              <button key={x.key} type="button" className={`dhf-fbtn${filter === x.key ? ' dhf-fbtn--on' : ''}`}
                      aria-pressed={filter === x.key} onClick={() => setFilter(x.key)}>
                {x.label}{x.key === 'diff' && s ? ` · ${s.changed + s.only_a + s.only_b + s.moved}` : ''}
              </button>
            ))}
          </div>
        </div>
        {s && (
          <div className="dhfv-cmp-sum">
            <span><b>{s.changed}</b> text differs</span>
            <span><b>{s.only_a}</b> only in {data.a.label}</span>
            <span><b>{s.only_b}</b> only in {data.b.label}</span>
            {s.moved > 0 && <span><b>{s.moved}</b> moved</span>}
            <span><b>{s.same}</b> the same</span>
          </div>
        )}
      </div>

      {error && <div className="drv-error" role="alert">{error}</div>}
      {note && <div className="dhf-result dhf-chip--good" role="status" style={{ padding: '8px 12px', borderRadius: 8 }}>{note}</div>}
      {a === b && <div className="dhf-warn">Choose two different sides.</div>}

      {data && (
        <div className="drv-card dhf-table-wrap">
          <table className="dhf-table dhfv-cmp-table">
            <thead>
              <tr><th style={{ width: 120 }}>Item</th><th>{data.a.label}</th><th>{data.b.label}</th><th style={{ width: 140 }}>Origin trace</th></tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const head = r.b || r.a;
                const showPara = i === 0 || (rows[i - 1].para !== r.para);
                return (
                  <React.Fragment key={`${head.nid}-${i}`}>
                    {showPara && r.para && <tr className="dhfv-cmp-para"><td colSpan={4}>{r.para}</td></tr>}
                    <tr className={`dhfv-cmp-${r.status}`}>
                      <td>
                        {head.key ? (head.url ? <a className="dhf-key" href={head.url} target="_blank" rel="noopener noreferrer">{head.key}</a> : <b>{head.key}</b>)
                          : <span className="dhfv-state dhfv-state--green">{r.kind === 'par' ? 'NEW §' : 'NEW'}</span>}
                        {r.status === 'moved' && <div className="dhfv-tiny">moved</div>}
                      </td>
                      <td>
                        {cell(r.a, r.b, r, 'a')}
                        {!r.a && r.b && a !== 'origin' && <button type="button" className="dhfv-act" onClick={() => copy(r, 'b', 'a')}>Copy to {data.a.label}</button>}
                        {r.status === 'changed' && a !== 'origin' && <button type="button" className="dhfv-act" onClick={() => copy(r, 'b', 'a')}>Use the right text here</button>}
                      </td>
                      <td>
                        {cell(r.b, r.a, r, 'b')}
                        {!r.b && r.a && b !== 'origin' && <button type="button" className="dhfv-act" onClick={() => copy(r, 'a', 'b')}>Copy to {data.b.label}</button>}
                        {r.status === 'changed' && b !== 'origin' && <button type="button" className="dhfv-act" onClick={() => copy(r, 'a', 'b')}>Use the left text here</button>}
                      </td>
                      <td>
                        {r.fact ? <>{coverageChip(r.fact)}{testsChip(r.fact.tests)}</>
                          : r.kind === 'item' && !r.origin_id ? <Chip tone="warn">Not traced</Chip> : null}
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
              {rows.length === 0 && <tr><td colSpan={4} className="drv-muted">No differences.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <div className="dhf-note" style={{ maxWidth: 900 }}>
        Any two can be compared: origin ↔ branch, or branch ↔ branch. <strong>Copy</strong> moves an item between branches
        (in this app only). Only <strong>Merge to Orcanos</strong> writes to Orcanos, and only into the origin.
      </div>
    </div>
  );
}
